import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";

/**
 * Nova Companion's on/off setting (FLIP-175), backed by the same NotificationSettings table Nova
 * Insight uses (FLIP-172) but under NotificationSettingType.NovaCompanion. Unlike Nova Insight,
 * Companion has no push/notification-time concept - it's a chat widget, not an alert - so the API
 * surface here is a plain bool rather than the full NotificationSettingDto shape.
 * Calls the two new SettingsController actions directly via HttpClient + API_BASE_URL, same
 * swagger-bypass pattern NovaInsightSettingsService already uses (regenerating the swagger client
 * isn't practical against a live API instance in this environment).
 */
@Injectable({ providedIn: "root" })
export class NovaCompanionSettingsService {
  /**
   * "Is Companion enabled" signal shared by the FAB, header badge and the settings page toggle.
   * Backed by a BehaviorSubject rather than a one-shot cached request so that saving a change on
   * the settings page updates the FAB/badge immediately, without a page reload - fetched once on
   * startup, then pushed forward by save(). Defaults to enabled on fetch error (no row / request
   * failure), matching the backend's fail-open default - Companion already ships visible to every
   * user, so a failed settings fetch shouldn't hide it.
   */
  private enabledSource = new BehaviorSubject<boolean>(true);
  enabled$: Observable<boolean> = this.enabledSource.asObservable();

  /** The current value, for a synchronous check (the onboarding lessons' eligibility). */
  get currentlyEnabled(): boolean {
    return this.enabledSource.value;
  }

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {
    this.get()
      .pipe(catchError(() => of(true)))
      .subscribe((isEnabled) => this.enabledSource.next(isEnabled));
  }

  get(): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.apiBaseUrl}/api/Settings/GetNovaCompanionSettings`
    );
  }

  save(isEnabled: boolean): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.apiBaseUrl}/api/Settings/SaveNovaCompanionSettings`,
        isEnabled
      )
      .pipe(tap((saved) => this.enabledSource.next(saved)));
  }
}
